// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld/base/core';
import L from 'leaflet';
import MywVectorLayerQuery from './mywVectorLayerQuery';
import 'myWorld/features/featureRepresentation';

const trace = myw.trace.bind(myw.trace, 'layer');

myw.MywVectorLayer = L.Layer.extend({
    options: {
        isStatic: false,
        updateFetchLimit: 500, // Largest number of features to fetch in a single query
        maxGCSize: 500 // Maximum number of features to remove in one GC call.
    },

    /**
     * @class  Layer that renders features in the myWorld database using vectors.  <br/>
     * Features for this table  will be drawn on the map via Leaflet vector layers: markers, Polylines or Polygons <br/>
     * Implements L.ILayer so that myw.MywVectorLayer can be added to a leaflet map directly (using the L.layerControl for example)
     * @param  {MyWorldDatasource}       datasource
     * @param  {StyleManager}   styleManager  Style manager instance to use to render this layer
     * @param  {vectorLayerOptions} options
     * @constructs
     */
    initialize(datasource, styleManager, options) {
        L.setOptions(this, options);

        // Don't set event handlers. Test extensively if this is changed.
        this.eventHandlers = [];

        /** reference to the datasource object
         * @type {MyWorldDatasource} */
        this.datasource = datasource;

        /** holds the feature representations displayed on the map. Keyed on feature URN
         * @type {Object.<string, FeatureRepresentation[]>} */
        this.featureRepresentations = {};

        /** Style manager to use for this layer
         * @type {StyleManager} */
        this.styleManager = styleManager;

        /** Indicates that this layer is visible (ie associated to a map)
         * @type {Boolean} */
        this.isVisible = false;

        this.query = new MywVectorLayerQuery(this, datasource);

        this.opacity = 1;

        this.lastZoom = null;

        _.bindAll(this, 'updateNoRedraw');
    },

    /**
     * Returns the feature type for this layer.
     * @return {string[]} Feature types to render on this layer
     */
    getFeatureTypes() {
        return this.options.featureTypes.map(ft => ft.name);
    },

    /**
     * Implementation of ILayer.onAdd.
     * Adds the feature representations to the map and refreshes self
     * @param  {L.Map} map
     */
    onAdd(map) {
        this.map = map;
        this.lastZoom = map.getZoom();
        this.isVisible = true;
        for (const id in this.featureRepresentations) {
            this.featureRepresentations[id].forEach(featureRep => {
                featureRep.addToMap(this.map);
            });
        }
        this.update();

        //set handlers for events that should referesh the features
        //We need to trigger on: zoom, drag(pan), resize and "goto"
        //the 'moveend' event triggers on all these situations
        map.on('moveend', this.updateNoRedraw);
    },

    /**
     * Sets opacity of all features
     */
    setOpacity(opacity) {
        this.opacity = opacity;
        for (const id in this.featureRepresentations) {
            _.invoke(this.featureRepresentations[id], 'setOpacity', opacity);
        }
    },

    /*
     * Needed because without setting a zoom limit,
     * the vectors disappear from the map when the zoom goes out of range
     * Copied this code from L.GridLayer
     * @param  {L.Map} map
     */
    beforeAdd(map) {
        map._addZoomLimit(this);
    },

    /**
     * Implementation of ILayer.onRemove
     * @param  {L.Map} map
     */
    onRemove(map) {
        map.off('moveend', this.updateNoRedraw);

        for (const id in this.featureRepresentations) {
            this.featureRepresentations[id].forEach(featureRep => {
                featureRep.removeFromMap();
            });
        }
        this.isVisible = false;
    },

    /**
     * Feature on layer has been modified. Update the actual feature rather than redrawing the whole layer
     * Note however that in some cases if the closing of the editor after the update results in
     * a map view resize then an update of the layer will occur regardless.
     *
     */
    featureModified(changeType, feature) {
        let featureReps;
        if (changeType == 'delete') {
            this.deleteRepsFor(feature);
        } else if (changeType == 'update') {
            featureReps = this.updateRepsFor(feature);
            if (featureReps) {
                featureReps.forEach(featureRep => {
                    featureRep.updateId = this.query.currentUpdateId;
                });
            }
        } else {
            featureReps = this.addRepFor(feature);
            featureReps.forEach(featureRep => {
                featureRep.updateId = this.query.currentUpdateId;
            });
        }
    },

    /**
     * Updates the layer from the database and refreshes the representations.
     * Called by layerManager.handleFeatureCollChanges()
     */
    redraw() {
        this.update(true);
    },

    updateNoRedraw() {
        Object.values(this.featureRepresentations).forEach(featureReps => {
            featureReps.forEach(featureRep => {
                if (this.shouldDisplayFeature(featureRep.feature, featureRep.geometryFieldName)) {
                    if (!featureRep.map) featureRep.addToMap(this.map);
                } else {
                    featureRep.removeFromMap();
                }
            });
        });

        if (this.options.isStatic) {
            const featureTypesInZoomRange = this._featuresTypesInNewZoomRange();
            if (featureTypesInZoomRange.length) {
                this.query.getFeaturesFromLastBounds(featureTypesInZoomRange.map(ft => ft.name));
            }
        }

        this.update(false);
    },

    /**
     * Refresh the layer
     * Obtains from the database the features relevant for the current map view.
     * The callback to process the results is handleLayerFeaturesResults
     * @param  {boolean} [redraw=false] Whether the existing feature representations should be refreshed as well
     */
    update(redraw = false) {
        redraw = redraw || !this.options.isStatic;

        if (!this.isVisible) return;

        this.query.update(redraw);
    },

    /**
     * Create a representation for the feature and add it to the map
     * @param {Feature} feature [description]
     */
    addRepFor(feature) {
        const urn = feature.getUrn(),
            featureReps = this.createRepsForFeature(feature);
        _.invoke(featureReps, 'setOpacity', this.opacity);
        if (featureReps && featureReps.length > 0) {
            if (this.map && this.isVisible) {
                featureReps.forEach(featureRep => {
                    featureRep.addToMap(this.map);
                });
            }
            this.featureRepresentations[urn] = featureReps;
        }
        return featureReps;
    },

    /**
     * Update feature representation for provided feature. Recalculate styles as these might depend
     * on feature attributes
     */
    updateRepsFor(feature) {
        const featureReps = this.featureRepresentations[feature.getUrn()];
        if (featureReps) this.updateReps(featureReps, feature);
        return featureReps;
    },

    updateReps(featureReps, feature) {
        featureReps.forEach(featureRep => {
            featureRep.styles = this.getStyleFor(feature, featureRep.geometryFieldName);
            featureRep.update(feature);
            featureRep.setOpacity(this.opacity);
            // Do this to ensure that marker icons are behind any highlight markers to ensure
            // the highlight markers get click events.
            // ENH: create a custom pane for markers and set the desired z-index on it instead of using bringToBack
            if (featureRep.map && featureRep.styles.normal && featureRep.styles.normal.icon)
                featureRep._overlay.bringToBack();
        });
    },

    deleteRepsFor(feature) {
        const urn = feature.getUrn(),
            featureReps = this.featureRepresentations[urn];
        if (featureReps) this.deleteReps(urn, featureReps);
    },

    deleteReps(urn, featureReps) {
        featureReps.forEach(featureRep => {
            featureRep.removeFromMap();
        });
        delete this.featureRepresentations[urn];
    },

    /**
     * Processes a list of features obtained from the database,
     * creating feature representations on the map control if necessary
     * @param  {boolean} redraw Whether the existing feature representations should be refreshed as well
     * @param  {Array<DDFeature>} features Features obtained from the database
     */
    handleLayerFeaturesResults(redraw, features, currentUpdateId) {
        features.forEach(feature => {
            const urn = feature.getUrn();
            let featureReps = this.featureRepresentations[urn];

            feature.layer = this; // associate the feature with its layer (this)

            if (!featureReps) {
                featureReps = this.addRepFor(feature);
                if (!featureReps) return;
            }

            featureReps.forEach(featureRep => {
                if (featureRep.map === null) {
                    //The representation has previously been removed from the map. Add it again
                    if (this.isVisible) featureRep.addToMap(this.map);
                } else if (redraw) {
                    this.updateReps([featureRep], feature);
                }

                featureRep.updateId = currentUpdateId;
            });
        });
    },

    /**
     * Remove feature representations that are not within map bounds plus a padding from the featureRepresentions list.
     * @param bounds Bounds of current map
     */
    gcFeatureRep(bounds, currentUpdateId) {
        const layerFeatureReps = this.featureRepresentations;
        trace(
            4,
            `Start gcFeatureRep layer='${this.options.name}' currentUpdateId=${currentUpdateId} ` +
                `#records=${_.size(layerFeatureReps)}`
        );
        bounds = bounds.pad(0.2);
        const urns = Object.keys(layerFeatureReps);
        this._asyncGcFeatureRep(urns, bounds, currentUpdateId);
    },

    /*
     * Remove feature representations that are not within given bounds
     * Does it asynchronously in chunks to prevent holding up the UI
     * @param bounds Bounds of current map
     */
    async _asyncGcFeatureRep(urns, bounds, currentUpdateId, start = 0) {
        //wait a little bit as to not hold up UI
        await Promise.delay(300);

        const layerFeatureReps = this.featureRepresentations;
        if (this.query.currentUpdateId !== currentUpdateId) {
            return trace(
                8,
                `Cancelled rep GC on '${this.options.name}' due to new update id (${this.query.currentUpdateId} vs ${currentUpdateId})`
            );
        }
        const upto = Math.min(start + this.options.maxGCSize, urns.length);
        let index;
        trace(
            8,
            `GC'ing '${this.options.name}' from ${start} to ${upto} (updateId: ${currentUpdateId})`
        );
        let collected = 0;
        for (index = start; index <= upto; index++) {
            const urn = urns[index];
            const individualFeatureReps = layerFeatureReps[urn];
            if (!individualFeatureReps) continue;

            let allRemoved = true;
            for (let featureRep of individualFeatureReps) {
                const remove = featureRep.map && !bounds.intersects(featureRep.getBounds());
                if (remove) featureRep.removeFromMap();
                else allRemoved = false;
            }
            if (allRemoved) {
                collected++;
                delete layerFeatureReps[urn];
            }
        }
        if (index < urns.length && index > this.options.maxGCSize) {
            //still more to GC (will do the delay as part of the call)
            return this._asyncGcFeatureRep(urns, bounds, currentUpdateId, index - 1);
        }
        //GC has been completed
        trace(
            4,
            `End gcFeatureRep layer='${this.options.name}' #records=${_.size(
                layerFeatureReps
            )} collected=${collected}`
        );
    },

    _getPrimaryRepresentation(featureReps) {
        return featureReps.find(
            featureRep =>
                featureRep.feature.getGeometryFieldNameInWorld(featureRep.worldName) ===
                featureRep.geometryFieldName
        );
    },

    /**
     * Creates and returns a representation for a feature
     * @param  {DDFeature} feature [description]
     * @return {FeatureRepresentation[]}
     */
    createRepsForFeature(feature) {
        const featureReps = [];
        const fieldNames = feature.getGeometryFieldNamesInWorld(this.options.worldName || 'geo');
        fieldNames.forEach(fieldName => {
            const rep = this.createRepForFeature(feature, fieldName);
            if (rep) featureReps.push(rep);
        });
        return featureReps;
    },

    /**
     * Creates and returns a representation for a feature
     * @param  {DDFeature} feature [description]
     * @return {FeatureRepresentation}
     */
    createRepForFeature(feature, geomFieldName) {
        const styles = this.getStyleFor(feature, geomFieldName);
        if (!styles) return;
        const geom = feature.getGeometry(geomFieldName);
        if (!geom) return;

        const options = {};
        options.eventHandlers = this.eventHandlers;
        options.styles = styles;
        options.worldName = this.options.worldName;
        options.geomFieldName = geomFieldName;

        if (geom.type === 'Point' || geom.type === 'MultiPoint') {
            options.textPaneName = this.options.textPaneName;
        }

        return new myw.FeatureRepresentation(feature, options);
    },

    /**
     * Removes from the map representations of deleted features <br/>
     * Should only called for 'redraw' requests (forced redraw or dynamic layer)
     * @param  {L.LatLngBounds} Bounds of the request to the database
     */
    removeDeletedFeatures(bounds, updateId) {
        // find features that weren't included in the last request's result but
        // have a representation intersecting the given bounds
        Object.keys(this.featureRepresentations).forEach(urn => {
            const featureReps = this.featureRepresentations[urn];
            // interested only in the featureRep that has a primary geom field
            const featureRep = this._getPrimaryRepresentation(featureReps);
            const isOldRep = featureRep && (!featureRep.updateId || featureRep.updateId < updateId);
            if (isOldRep && bounds.intersects(featureRep.getBounds())) {
                //the feature wasn't in the last set of results even though the bounds intersect,
                //this means one of:
                // - the feature has been deleted - representation should be removed
                // - the geometry does not intersect with the given bounds (although its bounds do)
                //     - in this situation this feature can still exist but not have been sent with
                //      latest results because the geom doesn't strictly intersect the map bounds
                //      ideally, we wouldn't remove the representation (so a map pan would still be able show it).
                //      This would require an actual intersection to be calculated (instead of just the bounds)
                this.deleteReps(urn, featureReps);
            }
        });
    },

    /**
     * Determine if the feature should be displayed, based on the visibilty fields from featureItem
     * @param  {DDFeature} feature
     * @param  {String} fieldName
     */
    shouldDisplayFeature(feature, fieldName) {
        const featureItem = this.getFeatureItemFor(feature, fieldName);
        if (!featureItem || (!featureItem.min_vis && !featureItem.max_vis)) return true;

        const withinMinRange = this.map.getZoom() >= featureItem.min_vis;
        const withinMaxRange = this.map.getZoom() <= featureItem.max_vis;

        if (featureItem.min_vis && featureItem.max_vis) return withinMinRange && withinMaxRange;
        if (featureItem.min_vis) return withinMinRange;
        if (featureItem.max_vis) return withinMaxRange;

        return false;
    },

    getFeatureItemFor(feature, geomFieldName) {
        return this.options.featureTypes.find(
            lfItem => lfItem.name == feature.type && geomFieldName == lfItem.field_name
        );
    },

    /**
     * Obtains the style apropriate for a given feature depending on the geometry type
     * @param  {DDFeature} feature
     * @param {string} geomFieldName
     * @return {styleDefinition}  Styles for normal and highlight states
     */
    getStyleFor(feature, geomFieldName) {
        const lfItem = this.getFeatureItemFor(feature, geomFieldName);

        if (!lfItem) return undefined;

        // Put layer properties on styleDef object
        const styleDef = { ...lfItem };
        styleDef.opacity = this.opacity;

        //Use feature definition for label visibility. If none exists use layer defn
        styleDef.max_vis = styleDef.max_vis || this.options.minZoom;
        styleDef.min_vis = styleDef.min_vis || this.options.minZoom;

        // Decode the style string from database into a structure
        const styleDefinition = this.styleManager.getStyleFor(feature, styleDef);
        // Pass to a possible custom method which may modify the style structure or return
        // a completely different one.
        return feature.getStyleDefinition(styleDefinition);
    },

    /**
     * Get feature types which should be visibile at current zoom.
     * @return {Array}
     * @private
     */
    _featuresTypesInNewZoomRange() {
        const currentZoom = this.map.getZoom();
        let featureTypesToRefresh = [];

        if (this.lastZoom && this.lastZoom != currentZoom) {
            //if the last zoom is out of range is the current zoom in range
            featureTypesToRefresh = this.options.featureTypes.filter(featureType => {
                if (featureType.min_vis || featureType.max_vis) {
                    if (featureType.min_vis && featureType.max_vis) {
                        return (
                            (this.lastZoom < featureType.min_vis ||
                                this.lastZoom > featureType.max_vis) &&
                            currentZoom >= featureType.min_vis &&
                            currentZoom <= featureType.max_vis
                        );
                    }

                    if (featureType.min_vis)
                        return (
                            this.lastZoom < featureType.min_vis &&
                            currentZoom >= featureType.min_vis
                        );
                    if (featureType.max_vis)
                        return (
                            this.lastZoom > featureType.max_vis &&
                            currentZoom <= featureType.max_vis
                        );
                }
                return true;
            });
        }
        this.lastZoom = this.map.getZoom();
        return featureTypesToRefresh;
    }
});

/**
 * Options for a Vector layer
 * @typedef vectorLayerOptions
 * @property  {string}          name                Name of the layer (for debugging)
 * @property  {boolean}         [isStatic=false]    Whether the layer is to be considered as static, <br/>
 *                                                  so performance optimizations can be used
 * @property  {Array<string>}   featureTypes        Feature types associated with the layer
 */

export default myw.MywVectorLayer;
